import { Button, DialogActions, DialogContent, DialogContentText, DialogTitle, Stack } from "@mui/material"
import Iconify from "minimals-template/components/Iconify"
import { useDialog } from "lib/@hooks/useDialog"
import { DraftSyncConflict } from "event-definitions"
import { useCallback } from "react"

function ConflictDialogContent({ ok, cancel }) {
    return (
        <>
            <DialogTitle>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Iconify icon="mdi:file-document-alert-outline" sx={{ color: "warning.main" }} />
                    <span>This schedule has changed elsewhere</span>
                </Stack>
            </DialogTitle>
            <DialogContent>
                <DialogContentText>
                    Someone else (or another session) has saved changes to this schedule since you started editing. You
                    can overwrite with your version, or discard your local changes and reload the latest from the
                    server.
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={cancel} startIcon={<Iconify icon="mdi:refresh" />}>
                    Discard my changes
                </Button>
                <Button onClick={() => ok("force")} variant="contained" startIcon={<Iconify icon="mdi:content-save" />}>
                    Use my version
                </Button>
            </DialogActions>
        </>
    )
}

/**
 * Hook that provides a conflict resolution dialog.
 * Returns a function that opens the dialog and resolves to "force" (overwrite) or undefined (discard).
 */
export function useConflictDialog() {
    return useDialog(<ConflictDialogContent />, { maxWidth: "sm", fullWidth: true })
}

/**
 * Hook that listens for DraftSyncConflict events and opens the conflict dialog automatically.
 * @param {object} options
 * @param {function} options.onForce - Called when user chooses to force-save
 * @param {function} options.onDiscard - Called when user chooses to discard changes
 */
export function useConflictResolution({ onForce, onDiscard }) {
    const openConflict = useConflictDialog()

    const handleConflict = useCallback(
        async (event) => {
            // In Cypress we auto-force to keep flows deterministic without modal timing races.
            if (typeof window !== "undefined" && window.Cypress) {
                if (onForce) await onForce(event)
                return
            }
            const result = await openConflict(event)
            if (result === "force") {
                if (onForce) await onForce(event)
            } else {
                if (onDiscard) await onDiscard(event)
            }
        },
        [openConflict, onForce, onDiscard]
    )

    DraftSyncConflict.useEvent(handleConflict, [handleConflict])
}
